'use client';

import { c as _c } from "react/compiler-runtime";
import { get } from 'es-toolkit/compat';
import { HomeIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { usePathname } from 'next/navigation';
import { type ReactNode, useCallback, useMemo } from 'react';
import { TitleBarNavLinkButton } from '../TitleBarNavLinkButton';
import { WindowControls } from '../WindowControls';
import { TitleBarButtonsLeft, TitleBarButtonsRight } from './TitleBarButtons';
import { TitleBarMenu } from './TitleBarMenu';
import { TitleBarUpdaterButton } from './TitleBarUpdaterButton';
import { USE_SETUP_WIZARD_FLOW } from '@/app/editor/featureFlags';
import { editorScene, project, store } from '@/app/editor/store';
import { globalStore } from '@/app/globalStore';
import { appInfo, isMac, isSdkParentWindow, platform, sdk } from '@/app/sdk';
import { Toggle } from '@/components/ui/toggle';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { EDITOR_VERSION, ENGINE_VERSION, PRODUCT_NAME } from '@/const';
import { cn, IS_DEV } from '@/lib/utils';
const SHOW_IN_SDK_POPUPS = true;

/**
 * Custom Electron title bar with left, center, and right widget slots.
 * Ensures native window controls (Windows) and macOS traffic lights are not blocked.
 */
export type TitleBarProps = {
  left?: ReactNode;
  center?: ReactNode;
  right?: ReactNode;
  className?: string;
};

/** Title bar for the Genesys Editor */
export const TitleBar = observer(({
  left,
  center,
  right,
  className
}: TitleBarProps) => {
  const pathname = usePathname();
  const isGameWindow = pathname.startsWith('/play/');

  // Hide TitleBar on game routes to provide full viewport for games, unless this is a popup window from the SDK
  if (isGameWindow && !(SHOW_IN_SDK_POPUPS && isSdkParentWindow())) {
    return null;
  }
  const leftMargin = isMac() ? 'ml-[72px]' : 'ml-2';
  const rightMargin = sdk?.window ? 'mr-0' : platform ? 'mr-[138px]' : 'mr-2';
  const isPlayMode = editorScene.isPlayMode;
  const isPlayModeStarting = editorScene.isPlayModeStarting;
  const showPlayModeTitleBar = isPlayMode || isPlayModeStarting;
  /** Hide editor chrome only once play mode is active; during start, match Save/Play visibility. */
  const hideEditorNavForPlayMode = isPlayMode;
  const disableNav = store.isBuildingProject || isPlayMode || isPlayModeStarting;
  const hasBuildErrorOverlay = globalStore.showBuildErrorOverlay && globalStore.hasBuildErrors;
  const hasPlayModeErrorOverlay = globalStore.showPlayModeErrorOverlay && globalStore.hasAnyPlayModeErrors;
  const shouldBlockTitleBarButtons = globalStore.loadingOverlay || hasBuildErrorOverlay || hasPlayModeErrorOverlay;
  const isMainWindow = useMemo(() => ['/dashboard', '/editor'].some(path => pathname.startsWith(path)), [pathname]);
  const editorOpened = useMemo(() => pathname.startsWith('/editor'), [pathname]);
  const isProjectLoaded = project.isProjectOpened;
  let domainName = new URL(window.location.href).host;
  domainName = appInfo?.isDev || !domainName.includes('genesys-ai') ? domainName : '';
  const title = useCallback((detailed: boolean) => {
    const gameName = isGameWindow ? get(window, 'gameMetadata.name') : undefined;
    const gameTitle = gameName ? `${gameName} - ${PRODUCT_NAME}` : undefined;
    if (showPlayModeTitleBar) {
      return gameTitle ?? [project.projectName, 'Play Mode'].filter(Boolean).join(' - ');
    }
    let version = '';
    if (detailed) {
      const engineVersion = `engine@${ENGINE_VERSION}`;
      const editorVersion = `editor@${EDITOR_VERSION}`;
      if (appInfo) {
        const sdkVersion = `sdk@${appInfo.appVersion}`;
        version = `${engineVersion} | ${sdkVersion} | ${editorVersion}`;
        if (appInfo.isDev) {
          version = IS_DEV ? `LOCAL-DEV ${version}` : `LOCAL-BUILD ${version}`;
        }
      } else {
        version = `${engineVersion} | ${editorVersion}`;
      }
    }
    return gameTitle ?? [project.currentScene ? project.sceneName : '', project.projectName, `${PRODUCT_NAME} ` + version + (detailed && domainName ? ` - ${domainName}` : '')].filter(Boolean).join(' - ');
  }, [domainName, isGameWindow, showPlayModeTitleBar]);
  return <header className={cn('relative z-50 flex h-(--titlebar-height) w-full select-none items-center backdrop-blur-[20px] p-0 titlebar dark transition-all duration-1000', showPlayModeTitleBar ? 'bg-black' : 'bg-(--titlebar-background-dark)', className)} data-slot="titlebar">
      {/* Left: Navigation and controls */}
      <div className={cn('flex items-center gap-2 pl-3 shrink-0', leftMargin, shouldBlockTitleBarButtons && 'pointer-events-none')}>
        {isMainWindow && sdk && <>
          {!hideEditorNavForPlayMode && <TitleBarMenu />}
          {!hideEditorNavForPlayMode && <Separator />}
          {!hideEditorNavForPlayMode && <TitleBarNavLinkButton targetPath="/dashboard" pathname={pathname} icon={HomeIcon} tooltip="Home" disabled={disableNav} />}
          {!hideEditorNavForPlayMode && USE_SETUP_WIZARD_FLOW && editorOpened && project.isProjectSelected && isProjectLoaded ? (/* Wizard flow: Editor + Wizard are paired view-switcher toggles */
        <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Toggle variant="titleBar" size="titleBar" pressed={store.activeView === 'editor'} onPressedChange={() => {
                  if (store.activeView === 'editor') return;
                  store.setActiveView('editor');
                }} className="titlebarControls" disabled={disableNav}>
                      <span className="text-sm font-normal px-1">Editor</span>
                    </Toggle>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Scene Editor</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Toggle variant="titleBar" size="titleBar" pressed={store.activeView === 'wizard'} onPressedChange={() => {
                  if (store.activeView === 'wizard') return;
                  store.setActiveView('wizard');
                }} className="titlebarControls" disabled={disableNav}>
                      <span className="text-sm font-normal px-1">Wizard</span>
                    </Toggle>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Setup Wizard</TooltipContent>
              </Tooltip>
            </>) : (/* Default flow: standard nav link to /editor */
        !hideEditorNavForPlayMode && <TitleBarNavLinkButton targetPath="/editor" pathname={pathname} tooltip={project.isProjectSelected ? 'Editor' : '⚠️ Select project first to enable editor'} disabled={disableNav || !project.isProjectSelected || !isProjectLoaded}>
              <span className="text-sm  font-normal px-1"> Editor </span>
            </TitleBarNavLinkButton>)}
        </>}
        <TitleBarButtonsLeft />
      </div>

      {/* Center: Title - absolutely positioned to center of full window */}
      {/* Show detailed title on xl+ (1280px+), simple title on smaller screens */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
        <span className={cn('hidden xl:inline truncate text-xs font-semibold text-foreground', showPlayModeTitleBar ? 'max-w-[30vw]' : 'max-w-[50vw]')}>
          {title(true)}
        </span>
        <span className={cn('inline xl:hidden truncate text-xs font-semibold text-foreground', showPlayModeTitleBar ? 'max-w-[30vw]' : 'max-w-[40vw]')}>
          {title(false)}
        </span>
      </div>

      {/* Spacer to push right controls to the end */}
      <div className="flex-1" />

      {/* Right: Controls - never shrink below content size */}
      <div className={cn('flex items-center justify-end gap-2 pr-2 titlebarControls shrink-0', rightMargin, shouldBlockTitleBarButtons && 'pointer-events-none')}>
        {isMainWindow && <TitleBarButtonsRight />}
        {isMainWindow && sdk && !showPlayModeTitleBar && <TitleBarUpdaterButton />}
        {platform !== 'darwin' && <WindowControls />}
      </div>
    </header>;
});
TitleBar.displayName = 'TitleBar';
const Separator = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-4 w-0.5 mx-2 bg-border" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};